import type { BrandStat } from "@/api/client";
import { cn } from "@/lib/utils";

interface WinsBarChartProps {
  stats: BrandStat[];
}

export function WinsBarChart({ stats }: WinsBarChartProps) {
  if (stats.length === 0) {
    return (
      <p className="text-[13px] text-slate-400 text-center py-6">
        No data yet.
      </p>
    );
  }

  const maxWins = Math.max(...stats.map((s) => s.wins), 1);

  return (
    <div className="space-y-4">
      {stats.map((stat) => {
        const isLeader = stat.wins === maxWins && stat.wins > 0;
        const widthPct = (stat.wins / maxWins) * 100;
        const winRate =
          stat.total_comparisons && stat.total_comparisons > 0
            ? Math.round((stat.wins / stat.total_comparisons) * 100)
            : null;

        return (
          <div key={stat.brand_id}>
            <div className="flex items-center justify-between mb-1.5">
              <span
                className={cn(
                  "text-[13px] font-medium",
                  isLeader ? "text-brand-950" : "text-slate-600"
                )}
              >
                {stat.brand_name}
              </span>
              <div className="flex items-center gap-2">
                {winRate !== null && (
                  <span className="text-[11px] text-slate-400 tabular-nums">
                    {winRate}%
                  </span>
                )}
                <span
                  className={cn(
                    "text-[13px] font-semibold tabular-nums",
                    isLeader ? "text-accent-700" : "text-brand-950"
                  )}
                >
                  {stat.wins}
                  <span className="font-normal text-slate-400 ml-0.5">
                    {stat.total_comparisons != null
                      ? ` / ${stat.total_comparisons}`
                      : ""}
                  </span>
                </span>
              </div>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-[5px] overflow-hidden">
              <div
                className={cn(
                  "h-[5px] rounded-full animate-score-fill",
                  isLeader ? "bg-accent-500" : "bg-brand-600"
                )}
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
